import * as Yup from "yup";

import {
  fieldConfigurations,
  FieldConfiguration,
  PIC_FIELD_INDICES,
  NOTES_FIELD_INDICES,
} from "./fieldConfigurations";
import { buildSideLoadedValidationSchema } from "./validationSchema";
import { normaliseColumnName } from "lib/sideLoadedFieldMapping";
import {
  parsePicValues,
  PIC_DISPLAY_DELIMITER,
} from "constants/sideLoadedBusinesses";

// A blank cell is not a mapping: the compare modal only offers columns that
// hold something, so a column mapped here but empty for this record would
// validate against a value the user cannot see.
const hasValue = (
  rawData: Record<string, string | number>,
  sourceColumn: string,
): boolean =>
  rawData[sourceColumn] !== undefined &&
  rawData[sourceColumn] !== null &&
  rawData[sourceColumn] !== "";

// A stored mapping wins over guessing, the same order of precedence the compare
// modal applies, so a record reads the same in the grid as it does in the modal.
function getClaimedColumns(
  configuration: FieldConfiguration,
  rawData: Record<string, string | number>,
  entries: SideLoadedDataMappingEntry[] | null | undefined,
): string[] {
  if (entries?.length) {
    return entries
      .filter(
        entry =>
          entry.targetFieldKey === configuration.value &&
          hasValue(rawData, entry.sourceColumn),
      )
      .map(entry => entry.sourceColumn);
  }

  return Object.keys(rawData).filter(
    sourceColumn =>
      configuration.sourceFieldSuggestions.includes(
        normaliseColumnName(sourceColumn),
      ) && hasValue(rawData, sourceColumn),
  );
}

export function getInitialAutoMappedValues(
  rawData: Record<string, string | number> = {},
  configurations: FieldConfiguration[] = Object.values(fieldConfigurations),
  entries?: SideLoadedDataMappingEntry[] | null,
): Record<string, string | number | boolean | null> {
  return configurations.reduce((acc, configuration) => {
    acc[`MappableField__${configuration.value}`] = null;
    acc[`Value__${configuration.value}`] = "";

    const claimedColumns = getClaimedColumns(
      configuration,
      rawData || {},
      entries,
    );

    if (configuration.handleAsNotes || configuration.handleAsProperty) {
      // Notes and PICs both gather several columns, into slots numbered their
      // own way. Columns past the last slot are dropped.
      const fieldIndices = configuration.handleAsProperty
        ? PIC_FIELD_INDICES
        : NOTES_FIELD_INDICES;
      if (claimedColumns.length) {
        acc[`MappableField__${configuration.value}`] = true;
        claimedColumns
          .slice(0, fieldIndices.length)
          .forEach((sourceColumn, position) => {
            acc[`MappableField__${configuration.value}__${fieldIndices[position]}`] =
              sourceColumn;
          });
      }
    } else if (claimedColumns.length) {
      // Last claim wins - one target takes a single column.
      const sourceColumn = claimedColumns[claimedColumns.length - 1];
      acc[`MappableField__${configuration.value}`] = sourceColumn;
      acc[`Value__${configuration.value}`] = rawData[sourceColumn];
    }

    if (configuration.handleAsProperty) {
      const picFieldKeys = PIC_FIELD_INDICES.map(
        index => `MappableField__${configuration.value}__${index}`,
      );
      const columnNames = picFieldKeys.map(k => acc[k]);
      if (columnNames.some(Boolean)) {
        acc[`Value__${configuration.value}`] = parsePicValues(
          columnNames.map(colName =>
            colName ? rawData[colName]?.toString() : undefined,
          ),
        ).join(PIC_DISPLAY_DELIMITER);
      }
    } else if (configuration.handleAsNotes) {
      const noteFieldKeys = NOTES_FIELD_INDICES.map(
        index => `MappableField__${configuration.value}__${index}`,
      );
      const noteColumnNames = noteFieldKeys.map(k => acc[k]);
      if (noteColumnNames.some(Boolean)) {
        acc[`Value__${configuration.value}`] = noteColumnNames
          .filter(fieldName => fieldName && rawData[fieldName] !== undefined)
          .map(fieldName => `${fieldName}: ${rawData[fieldName]}`)
          .join("\n");
      }
    }

    return acc;
  }, {});
}

export interface SideLoadedValidationOptions {
  businessIdByAgentCodeLookup?: Record<string, string>;
  businessIdBySaleyardCodeLookup?: Record<string, string>;
  configurations?: FieldConfiguration[];
  entries?: SideLoadedDataMappingEntry[] | null;
}

export function getSideLoadedBusinessValidationIssues(
  record: SideLoadedBusiness,
  {
    businessIdByAgentCodeLookup = {},
    businessIdBySaleyardCodeLookup = {},
    configurations = Object.values(fieldConfigurations),
    entries = null,
  }: SideLoadedValidationOptions = {},
): string[] {
  const rawData = record?.rawData || {};
  const values = getInitialAutoMappedValues(rawData, configurations, entries);
  const schema = buildSideLoadedValidationSchema(configurations, {
    rawData,
    businessIdByAgentCodeLookup,
    businessIdBySaleyardCodeLookup,
  });

  try {
    schema.validateSync(values, { abortEarly: false });
    return [];
  } catch (error) {
    if (error instanceof Yup.ValidationError) {
      return error.errors;
    }
    throw error;
  }
}
